// Exchange API client — connects to the blockchain connector service via API gateway
import apiClient from './client';
import type { ExchangeBalance } from '../types/exchange';

export type { ExchangeBalance };

export interface ExchangeRouteGuidanceEntry {
  route_role: 'proof_owner' | 'management_owner' | 'summary_only' | 'ledger_only';
  handoff_route: '/exchange-sync' | null;
  primary_next_step: 'sync_now' | 'retry_sync' | 'manage_connection' | null;
  dominant_action: 'review_last_successful_sync' | 'sync_now' | 'retry_sync' | 'manage_connection' | null;
  safe_retry_guidance: {
    code: string;
    retryable_now: boolean;
    retry_action: 'sync_now' | 'retry_sync' | 'manage_connection' | null;
    dominant_action: 'review_last_successful_sync' | 'sync_now' | 'retry_sync' | 'manage_connection' | null;
  };
}

export interface ExchangeConnection {
  id: string;
  user_id: string;
  exchange: 'binance' | 'coinbase' | 'kraken' | 'bybit' | 'okx' | 'gemini';
  label: string | null;
  status: 'active' | 'error' | 'revoked';
  last_synced_at: string | null;
  error_message: string | null;
  balance_snapshot: ExchangeBalance[] | null;
  created_at: string;
  updated_at: string;
  api_key: string; // Always masked as '****' from server
  sync_proof_connection_id?: string | null;
  last_successful_sync_at?: string | null;
  sync_health_state?: 'healthy' | 'stale' | 'blocked' | 'never_synced' | null;
  sync_health_reason?:
    | 'recent_successful_sync'
    | 'stale_sync'
    | 'sync_error'
    | 'never_synced'
    | 'provider_credentials_required'
    | 'provider_auth_required'
    | 'provider_replay_blocked'
    | null;
  sync_health_blocker?: 'SAFE_EXCHANGE_REPLAY_BLOCKED' | null;
  sync_blocker_classification?: 'safe_exchange_replay_blocked' | 'credential_action' | 'auth_action' | null;
  sync_freshness_bucket?: 'fresh' | 'stale' | 'never_synced' | null;
  sync_freshness_stale_hours?: number | null;
  sync_freshness_state?: 'healthy' | 'stale_verified' | 'never_synced' | null;
  sync_primary_next_step?: 'sync_now' | 'retry_sync' | 'manage_connection' | null;
  sync_dominant_action?: 'review_last_successful_sync' | 'sync_now' | 'retry_sync' | 'manage_connection' | null;
  sync_last_successful_sync_proof?: {
    available: boolean;
    synced_at: string | null;
    freshness_bucket: 'fresh' | 'stale' | 'never_synced';
    freshness_stale_hours: number | null;
    proof_source: 'exchange_api_connections';
  } | null;
  sync_credential_action_required?: boolean;
  sync_provider_auth_action_required?: boolean;
  sync_provider_replay_blocked?: boolean;
  sync_runner_wait_state?: 'runner_limited_wait' | null;
  sync_runner_wait_message?: string | null;
  sync_safe_retry_guidance?: {
    code: string;
    retryable_now: boolean;
    retry_action: 'sync_now' | 'retry_sync' | 'manage_connection' | null;
    dominant_action: 'review_last_successful_sync' | 'sync_now' | 'retry_sync' | 'manage_connection' | null;
  } | null;
  sync_proof_authority?: 'mounted_last_successful_sync' | null;
  sync_provider_replay_scope?: 'management_owned_path' | null;
  sync_route_guidance?: {
    import: ExchangeRouteGuidanceEntry;
    management: ExchangeRouteGuidanceEntry;
    summary: ExchangeRouteGuidanceEntry;
    ledger: ExchangeRouteGuidanceEntry;
  } | null;
  last_sync_imported_count?: number | null;
  last_sync_skipped_count?: number | null;
  last_sync_error_count?: number | null;
  last_sync_source_label?: string | null;
}

export interface ConnectExchangePayload {
  userId: string;
  exchange: 'binance' | 'coinbase' | 'kraken' | 'bybit' | 'okx' | 'gemini';
  apiKey: string;
  apiSecret: string;
  passphrase?: string;
  label?: string;
}

export interface ConnectExchangeResponse extends ExchangeConnection {
  balanceCount?: number;
}

export interface ListConnectionsResponse {
  connections: ExchangeConnection[];
}

/**
 * Register a new exchange connection. Tests the connection before storing.
 * API keys are encrypted at rest and never returned after this call.
 */
export async function connectExchange(data: ConnectExchangePayload): Promise<ConnectExchangeResponse> {
  const response = await apiClient.post<ConnectExchangeResponse>(
    '/blockchain/exchange/connections',
    data
  );
  return response.data;
}

/**
 * List all active exchange connections for a user (keys masked as ****).
 */
export async function listExchangeConnections(userId: string): Promise<ListConnectionsResponse> {
  const response = await apiClient.get<ListConnectionsResponse>(
    `/blockchain/exchange/connections/${userId}`
  );
  return response.data;
}

/**
 * Revoke an exchange connection. Immediately stops background sync.
 */
export async function revokeExchange(id: string): Promise<void> {
  await apiClient.delete(`/blockchain/exchange/connections/${id}`);
}

/**
 * Trigger a manual sync for a specific exchange connection.
 */
export async function syncExchange(id: string): Promise<{ message: string; connectionId: string }> {
  const response = await apiClient.post<{ message: string; connectionId: string }>(
    `/blockchain/exchange/connections/${id}/sync`
  );
  return response.data;
}

export interface ExchangeFreshnessEntry {
  connectionId: string;
  exchange: string;
  last_synced_at: string | null;
  isStale: boolean;
  staleHours: number | null;
  health_state?: 'healthy' | 'stale' | 'blocked' | 'never_synced';
  health_reason?:
    | 'recent_successful_sync'
    | 'stale_sync'
    | 'sync_error'
    | 'never_synced'
    | 'provider_credentials_required'
    | 'provider_auth_required'
    | 'provider_replay_blocked';
  health_blocker?: 'SAFE_EXCHANGE_REPLAY_BLOCKED' | null;
  blocker_classification?: 'safe_exchange_replay_blocked' | 'credential_action' | 'auth_action' | null;
  primary_next_step?: 'sync_now' | 'retry_sync' | 'manage_connection' | null;
  dominant_action?: 'review_last_successful_sync' | 'sync_now' | 'retry_sync' | 'manage_connection' | null;
  proof_source?: 'exchange_api_connections';
  freshness_state?: 'healthy' | 'stale_verified' | 'never_synced';
  last_successful_sync_proof?: {
    available: boolean;
    synced_at: string | null;
    freshness_bucket: 'fresh' | 'stale' | 'never_synced';
    freshness_stale_hours: number | null;
    proof_source: 'exchange_api_connections';
  };
  credential_action_required?: boolean;
  provider_auth_action_required?: boolean;
  provider_replay_blocked?: boolean;
  runner_wait_state?: 'runner_limited_wait' | null;
  runner_wait_message?: string | null;
  safe_retry_guidance?: {
    code: string;
    retryable_now: boolean;
    retry_action: 'sync_now' | 'retry_sync' | 'manage_connection' | null;
    dominant_action: 'review_last_successful_sync' | 'sync_now' | 'retry_sync' | 'manage_connection' | null;
  };
  proof_authority?: 'mounted_last_successful_sync';
  provider_replay_scope?: 'management_owned_path' | null;
  route_guidance?: {
    import: ExchangeRouteGuidanceEntry;
    management: ExchangeRouteGuidanceEntry;
    summary: ExchangeRouteGuidanceEntry;
    ledger: ExchangeRouteGuidanceEntry;
  };
}

export interface ExchangeFreshnessResponse {
  freshness: ExchangeFreshnessEntry[];
  health_by_exchange?: Record<
    string,
    {
      connectionId: string;
      exchange: string;
      last_synced_at: string | null;
      health_state: 'healthy' | 'stale' | 'blocked' | 'never_synced';
      health_reason:
        | 'recent_successful_sync'
        | 'stale_sync'
        | 'sync_error'
        | 'never_synced'
        | 'provider_credentials_required'
        | 'provider_auth_required'
        | 'provider_replay_blocked';
      health_blocker: 'SAFE_EXCHANGE_REPLAY_BLOCKED' | null;
      blocker_classification: 'safe_exchange_replay_blocked' | 'credential_action' | 'auth_action' | null;
      primary_next_step: 'sync_now' | 'retry_sync' | 'manage_connection' | null;
      dominant_action: 'review_last_successful_sync' | 'sync_now' | 'retry_sync' | 'manage_connection' | null;
      proof_source: 'exchange_api_connections';
      freshness_bucket: 'fresh' | 'stale' | 'never_synced';
      freshness_stale_hours: number | null;
      freshness_state: 'healthy' | 'stale_verified' | 'never_synced';
      last_sync_imported_count: number | null;
      last_sync_skipped_count: number | null;
      last_sync_error_count: number | null;
      last_sync_source_label: string;
      last_successful_sync_proof: {
        available: boolean;
        synced_at: string | null;
        freshness_bucket: 'fresh' | 'stale' | 'never_synced';
        freshness_stale_hours: number | null;
        proof_source: 'exchange_api_connections';
      };
      credential_action_required: boolean;
      provider_auth_action_required: boolean;
      provider_replay_blocked: boolean;
      runner_wait_state?: 'runner_limited_wait' | null;
      runner_wait_message?: string | null;
      safe_retry_guidance: {
        code: string;
        retryable_now: boolean;
        retry_action: 'sync_now' | 'retry_sync' | 'manage_connection' | null;
        dominant_action: 'review_last_successful_sync' | 'sync_now' | 'retry_sync' | 'manage_connection' | null;
      };
      proof_authority?: 'mounted_last_successful_sync';
      provider_replay_scope?: 'management_owned_path' | null;
      route_guidance?: {
        import: ExchangeRouteGuidanceEntry;
        management: ExchangeRouteGuidanceEntry;
        summary: ExchangeRouteGuidanceEntry;
        ledger: ExchangeRouteGuidanceEntry;
      };
    }
  >;
}

/**
 * Fetch exchange connection freshness data for a user.
 * Returns staleness info for each active exchange connection.
 */
export async function getExchangeFreshness(userId: string): Promise<ExchangeFreshnessResponse> {
  const response = await apiClient.get<ExchangeFreshnessResponse>(
    `/analytics/exchange-sync/freshness/${userId}`
  );
  return response.data;
}

export interface ExchangeBalanceTotalEntry {
  exchangeId: string;
  name: string;
  label: string | null;
  totalValueUsd: number;
  lastSyncedAt: string | null;
}

export interface ExchangeBalanceTotalResponse {
  totalValueUsd: number;
  byExchange: ExchangeBalanceTotalEntry[];
}

/**
 * Fetch aggregated exchange balance totals from the latest snapshots.
 * Returns overall total and per-exchange breakdown for Dashboard integration.
 */
export async function getExchangeBalanceTotal(userId: string): Promise<ExchangeBalanceTotalResponse> {
  const response = await apiClient.get<ExchangeBalanceTotalResponse>(
    `/blockchain/exchange/balance-total/${userId}`
  );
  return response.data;
}
